// src/components/GreetingCard/EditableText.jsx
import React, { useRef, useEffect, useState } from "react";
import Konva from "konva";
import { Text as KonvaText } from "react-konva";
import { CARD_W, CARD_H } from "../../utils/geometry";

export default function EditableText({ textProps, onChange, bounds = null, imageBounds = null }) {
  const textRef  = useRef(null);
  const tweenRef = useRef(null);
  // Initialize hasBeenManuallyPositioned to true if we have saved coordinates
  // This prevents auto-centering from overwriting saved positions on page load
  const [hasBeenManuallyPositioned, setHasBeenManuallyPositioned] = useState(
    () => typeof textProps.x === 'number' && typeof textProps.y === 'number'
  );
  const [lastTextContent, setLastTextContent] = useState(textProps.text);

  // Use provided bounds or default to full card dimensions hmmm
  const {
    x: boundX = 0,
    y: boundY = 0,
    width: boundW = CARD_W,
    height: boundH = CARD_H,
  } = bounds || {};

  // Calculate centered X position
  // For text with align="center" and width matching bounds, x should just be boundX
  // because Konva automatically centers text within its width container
  const getCenteredX = () => {
    const node = textRef.current;
    if (!node) return boundX;
    
    // If text has align="center" and width matches bounds, just use boundX
    // The text will auto-center within its container
    const textWidth = textProps.width || 0;
    if (textProps.align === "center" && Math.abs(textWidth - boundW) < 10) {
      return boundX;
    }
    
    // Otherwise, calculate based on actual rendered width
    const box = node.getClientRect();
    const stageScaleX = node.getStage()?.scaleX() || 1;
    return boundX + (boundW - box.width / stageScaleX) / 2;
  };

  const getCenteredY = () => {
    const node = textRef.current;
    if (!node) return boundY;
    const box = node.getClientRect();
    const stageScaleY = node.getStage()?.scaleY() || 1;
    return boundY + (boundH - box.height / stageScaleY) / 2;
  };

  // Constrain text to bounds, accounting for text dimensions and animations
  const dragBound = (pos) => {
    const node = textRef.current;
    if (!node) return pos;

    // If text is not centered (inside text), allow free movement
    if (textProps.align !== "center") {
      return pos;
    }

    // Get current text dimensions including any active animations
    const box = node.getClientRect();
    const scale = node.getStage()?.scaleX() || 1;
    const actualWidth = box.width / scale;
    const height = box.height / scale;

    // For centered text with a fixed width, use the container width for bounds calculation
    // This prevents the text from shifting when the actual rendered text is narrower
    const textContainerWidth = textProps.width || actualWidth;
    const width = Math.max(actualWidth, textContainerWidth);

    // Account for any active animations that might affect size
    const animScale = textProps.animStyle === "bounce" ? 1.04 : 1;
    const safeWidth = width * animScale;
    const safeHeight = height * animScale;

    // Use image bounds if provided (for cover text), otherwise use padding bounds
    const effectiveBounds = imageBounds || bounds || { x: 0, y: 0, width: CARD_W, height: CARD_H };

    // Calculate allowed range - be more permissive with vertical movement
    const minX = effectiveBounds.x;
    const maxX = effectiveBounds.x + effectiveBounds.width - safeWidth;
    
    // Allow more vertical freedom - just ensure text center stays within image
    const textCenterY = pos.y + (safeHeight / 2);
    const boundsCenterY = effectiveBounds.y + (effectiveBounds.height / 2);
    const maxDistance = effectiveBounds.height / 2 + safeHeight; // Allow text to extend beyond bounds
    const deltaY = textCenterY - boundsCenterY;
    const clampedDeltaY = Math.max(-maxDistance, Math.min(maxDistance, deltaY));
    const boundedY = boundsCenterY + clampedDeltaY - (safeHeight / 2);

    // Ensure text stays within bounds horizontally but has more freedom vertically
    return {
      x: Math.max(minX, Math.min(maxX, pos.x)),
      y: boundedY
    };
  };

  // Reset manual positioning when text content changes significantly
  useEffect(() => {
    if (textProps.text !== lastTextContent) {
      setLastTextContent(textProps.text);
      // Reset manual positioning for significant text changes (but not for small edits)
      // Make this more conservative to avoid resetting too often
      if (Math.abs((textProps.text || "").length - (lastTextContent || "").length) > 5) {
        setHasBeenManuallyPositioned(false);
      }
    }
  }, [textProps.text, lastTextContent]);

  // Auto-center cover text, but only if it hasn't been manually positioned
  useEffect(() => {
    const node = textRef.current;
    if (!node) return;

    // Only auto-center cover text (align === "center") that hasn't been manually moved
    // AND only if dragging is enabled (not in preview mode)
    if (textProps.align === "center" && 
        !hasBeenManuallyPositioned && 
        textProps.draggable !== false) {
      
      // Small delay to ensure text is rendered
      const timeoutId = setTimeout(() => {
        const currentNode = textRef.current;
        if (!currentNode) return;
        
        const centeredX = getCenteredX();
        const centeredY = getCenteredY();
        
        // Only update if the position actually changed to avoid loops
        if (Math.abs(currentNode.x() - centeredX) > 1 || Math.abs(currentNode.y() - centeredY) > 1) {
          // Ensure centered position is within bounds
          const boundedPos = dragBound({ x: centeredX, y: centeredY });
          currentNode.x(boundedPos.x);
          currentNode.y(boundedPos.y);
          onChange({ ...textProps, x: boundedPos.x, y: boundedPos.y });
          currentNode.getLayer()?.batchDraw();
        }
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [textProps.align, hasBeenManuallyPositioned, textProps.draggable, boundX, boundY, boundW, boundH]);

  // Wait for the font to load before first draw (for cover text)
  useEffect(() => {
    if (!textProps.fontFamily || 
        textProps.align !== "center" || 
        hasBeenManuallyPositioned || 
        textProps.draggable === false ||
        (typeof textProps.x === 'number' && typeof textProps.y === 'number')) return;
    
    const fam = textProps.fontFamily.split(",")[0].replace(/['"]/g, "").trim();
    document.fonts
      .load(`${textProps.fontSize}px "${fam}"`)
      .then(() => {
        const node = textRef.current;
        if (node && textProps.align === "center" && !hasBeenManuallyPositioned && textProps.draggable !== false) {
          // Center the cover text after font loads
          const centeredX = getCenteredX();
          const centeredY = getCenteredY();
          
          // Only update if the position actually changed to avoid loops
          if (Math.abs(node.x() - centeredX) > 1 || Math.abs(node.y() - centeredY) > 1) {
            // Ensure centered position is within bounds
            const boundedPos = dragBound({ x: centeredX, y: centeredY });
            node.x(boundedPos.x);
            node.y(boundedPos.y);
            onChange({ ...textProps, x: boundedPos.x, y: boundedPos.y });
            node.getLayer()?.batchDraw();
          }
        }
      })
      .catch(console.warn);
  }, [textProps.fontFamily, textProps.fontSize]);

  // Play animations while ensuring text stays within bounds
  useEffect(() => {
    tweenRef.current?.destroy();
    tweenRef.current = null;
    const node = textRef.current;
    if (!node) return;

    // Get current position and ensure it's within bounds before starting animation
    const currentPos = dragBound({ x: node.x(), y: node.y() });
    node.x(currentPos.x);
    node.y(currentPos.y);

    switch (textProps.animStyle) {
      case "bounce": {
        // Calculate safe scale that won't push text out of bounds
        const box = node.getClientRect();
        const scale = node.getStage()?.scaleX() || 1;
        const width = box.width / scale;
        const height = box.height / scale;
        
        // Use image bounds if provided (for cover text), otherwise use padding bounds
        const effectiveBounds = imageBounds || bounds || { x: 0, y: 0, width: CARD_W, height: CARD_H };
        
        // Calculate maximum safe scale - subtle bounce
        const maxScaleX = (effectiveBounds.width - (currentPos.x - effectiveBounds.x)) / width;
        const maxScaleY = (effectiveBounds.height - (currentPos.y - effectiveBounds.y)) / height;
        const safeScale = Math.min(1.04, maxScaleX, maxScaleY);

        tweenRef.current = new Konva.Tween({
          node,
          scaleX: safeScale,
          scaleY: safeScale,
          duration: 2.5,
          easing: Konva.Easings.EaseInOut,
          yoyo: true,
          repeat: -1,
        }).play();
        break;
      }

      case "wiggle": {
        // Fast, symmetrical wiggle animation (-2° to +2°)
        const baseRotation = textProps.rotation || 0;
        
        // Start at -2° from base, animate to +2° from base
        node.rotation(baseRotation - 2);
        
        tweenRef.current = new Konva.Tween({
          node,
          rotation: baseRotation + 2,
          duration: 0.25,  // Fast - 0.5s total for full cycle (0.25s each way)
          easing: Konva.Easings.EaseInOut,
          yoyo: true,
          repeat: -1,
        }).play();
        break;
      }

      default:
        break;
    }

    return () => tweenRef.current?.destroy();
  }, [textProps.animStyle, bounds, imageBounds]);

  // Debug logging - remove after fixing
  console.log('[EditableText] Render debug:', {
    text: textProps.text,
    x: textProps.x,
    y: textProps.y,
    fill: textProps.fill,
    fontSize: textProps.fontSize,
    fontFamily: textProps.fontFamily,
    width: textProps.width,
    align: textProps.align,
    hasBeenManuallyPositioned,
    bounds: { boundX, boundY, boundW, boundH }
  });

  return (
    <KonvaText
      ref={textRef}
      {...textProps}
      draggable={textProps.draggable !== false}
      dragBoundFunc={dragBound}
      onDragStart={() => {
        // Mark as manually positioned when user starts dragging
        setHasBeenManuallyPositioned(true);
      }}
      onDragMove={(e) => {
        const node = e.target;
        const { x, y } = node.position();
        onChange({ ...textProps, x: Math.round(x), y: Math.round(y) });
      }}
      onDragEnd={(e) => {
        const node = e.target;
        const { x, y } = node.position();
        onChange({ ...textProps, x: Math.round(x), y: Math.round(y) });
      }}
      onDblClick={(e) => {
        e.cancelBubble = true;
        if (textProps.draggable === false) return;
        const t = prompt("Edit text", textProps.text);
        if (t !== null) {
          onChange({ ...textProps, text: t });
        }
      }}
      listening={true}
      perfectDrawEnabled={false}
    />
  );
}
